import { Badge, Button, DataTable, LoadingIndicator } from '@commenda/kakashi';
import { useMemo } from 'react';

import { IndirectTaxFilingsStatusHelper } from '../helpers';
import { IndirectTaxFilingsStatusBanner } from './IndirectTaxFilingsStatusBanner';
import { IndirectTaxFilingsStatusIcon } from './IndirectTaxFilingsStatusIcon';
import { IndirectTaxFilingsStatusBreakdownDisplayRow } from './useIndirectTaxFilingsStatusModal';

interface IndirectTaxFilingsStatusBreakdownTableProps {
  rows: IndirectTaxFilingsStatusBreakdownDisplayRow[];
  filingCount: number;
  isLoading: boolean;
  isRerunInProgress: boolean;
  isAggregateSyncInProgress: boolean;
  onRecheckStatus: () => void;
  onRerunBreakdownGeneration: () => void;
  onSyncAggregates: () => void;
  /**
   * Optional click handler — when provided, rows become clickable and the
   * dashboard wires this to navigate the user to the filing detail page.
   */
  onFilingClick?: (filingId: string) => void;
  /**
   * Admin-only: when true, rows with failed breakdowns get a "View error"
   * button that resolves the per-invoice errors for the filing. Off by default
   * so customer apps never surface it.
   */
  showJobErrorActions?: boolean;
  onViewBreakdownErrors?: (filingId: string, label: string) => void;

  onBreakdownAnalysisRun?: (filingId: string, label: string) => void;
}

/**
 * US/Canada tab body. Renders the verdict-driven status banner above the
 * filing-level rollup table. Four metadata columns (filing / tax period /
 * due date / status verdict) plus the five invoice-level breakdown
 * counters; banner gives the operator the same re-check + re-run actions
 * the legacy sales-tax modal had.
 */
const IndirectTaxFilingsStatusBreakdownTable = ({
  rows,
  filingCount,
  isLoading,
  isRerunInProgress,
  isAggregateSyncInProgress,
  onRecheckStatus,
  onRerunBreakdownGeneration,
  onSyncAggregates,
  onFilingClick,
  showJobErrorActions = false,
  onViewBreakdownErrors,
  onBreakdownAnalysisRun,
}: IndirectTaxFilingsStatusBreakdownTableProps) => {
  const columnGroups = useMemo(
    () =>
      buildColumnGroups({
        showJobErrorActions,
        onViewBreakdownErrors,
        onBreakdownAnalysisRun,
      }),
    [showJobErrorActions, onViewBreakdownErrors, onBreakdownAnalysisRun]
  );

  const aggregateVerdict = useMemo(
    () => IndirectTaxFilingsStatusHelper.getAggregateBreakdownVerdict(rows),
    [rows]
  );
  const failedCount = useMemo(
    () =>
      IndirectTaxFilingsStatusHelper.countFailures(
        rows.map((row) =>
          IndirectTaxFilingsStatusHelper.getBreakdownVerdict(row)
        )
      ),
    [rows]
  );

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-10xl">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-xl">
      <IndirectTaxFilingsStatusBanner
        verdict={aggregateVerdict}
        filingCount={filingCount}
        failedCount={failedCount}
        rerunActionLabel="Re-run breakdown generation"
        isRerunInProgress={isRerunInProgress}
        onRecheckStatus={onRecheckStatus}
        onRerunJob={onRerunBreakdownGeneration}
        tertiaryAction={{
          label: 'Sync aggregates',
          onClick: onSyncAggregates,
          isInProgress: isAggregateSyncInProgress,
        }}
      />
      <DataTable
        columnGroups={columnGroups}
        data={rows}
        searchConfig={{
          isEnabled: true,
          columnsToEnableSearchFor: ['filingNameLabel', 'jurisdictionLabel'],
          placeholder: 'Search filing or jurisdiction',
        }}
        emptyStateConfig={{
          title: 'No breakdown progress yet',
          description:
            'Trigger a tax breakdown generation for these filings to populate status.',
        }}
        onRowClick={
          onFilingClick
            ? (row: IndirectTaxFilingsStatusBreakdownDisplayRow) =>
                onFilingClick(row.filingId)
            : undefined
        }
      />
    </div>
  );
};

function buildColumnGroups({
  showJobErrorActions,
  onViewBreakdownErrors,
  onBreakdownAnalysisRun,
}: {
  showJobErrorActions: boolean;
  onViewBreakdownErrors?: (filingId: string, label: string) => void;
  onBreakdownAnalysisRun?: (filingId: string, label: string) => void;
}) {
  type Row = IndirectTaxFilingsStatusBreakdownDisplayRow;
  const groups = [
    {
      id: 'breakdown-info',
      columns: [
        {
          accessorKey: 'filingNameLabel',
          header: 'Filing',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => {
            const verdict = IndirectTaxFilingsStatusHelper.getBreakdownVerdict(
              row.original
            );
            return (
              <div className="flex items-center gap-sm">
                <IndirectTaxFilingsStatusIcon verdict={verdict} />
                <span className="txt-small-medium text-text-primary">
                  {row.original.filingNameLabel}
                </span>
              </div>
            );
          },
        },
        {
          accessorKey: 'jurisdictionLabel',
          header: 'Jurisdiction',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <span className="txt-small text-text-secondary">
              {row.original.jurisdictionLabel}
            </span>
          ),
        },
        {
          accessorKey: 'taxPeriodLabel',
          header: 'Tax period',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <span className="txt-small text-text-secondary">
              {row.original.taxPeriodLabel}
            </span>
          ),
        },
        {
          accessorKey: 'filingDueDateLabel',
          header: 'Due date',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <span className="txt-small text-text-secondary">
              {row.original.filingDueDateLabel}
            </span>
          ),
        },
        {
          accessorKey: 'status',
          header: 'Status',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) => {
            const verdict = IndirectTaxFilingsStatusHelper.getBreakdownVerdict(
              row.original
            );
            return (
              <Badge
                variant={IndirectTaxFilingsStatusHelper.getBadgeVariant(
                  verdict
                )}
              >
                {IndirectTaxFilingsStatusHelper.getStatusLabel(verdict)}
              </Badge>
            );
          },
        },
      ],
    },
    {
      id: 'breakdown-counters',
      columns: [
        {
          accessorKey: 'totalInvoicesInRange',
          header: 'Total invoices',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="gray">{row.original.totalInvoicesInRange}</Badge>
          ),
        },
        {
          accessorKey: 'totalBreakdownsCompleted',
          header: 'Completed',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="success">
              {row.original.totalBreakdownsCompleted}
            </Badge>
          ),
        },
        {
          accessorKey: 'totalBreakdownsInProgress',
          header: 'In progress',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="blue">
              {row.original.totalBreakdownsInProgress}
            </Badge>
          ),
        },
        {
          accessorKey: 'totalBreakdownsFailed',
          header: 'Failed',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="error">{row.original.totalBreakdownsFailed}</Badge>
          ),
        },
        {
          accessorKey: 'totalBreakdownsNotStarted',
          header: 'Not started',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="gray">
              {row.original.totalBreakdownsNotStarted}
            </Badge>
          ),
        },
      ],
    },
  ];

  if (showJobErrorActions) {
    groups.push({
      id: 'breakdown-actions',
      columns: [
        {
          accessorKey: 'filingId',
          header: '',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) =>
            row.original.totalBreakdownsFailed > 0 ? (
              <div className="flex items-center gap-sm">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onViewBreakdownErrors?.(
                      row.original.filingId,
                      row.original.filingNameLabel
                    );
                  }}
                >
                  View error
                </Button>
                {onBreakdownAnalysisRun ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onBreakdownAnalysisRun(
                        row.original.filingId,
                        row.original.filingNameLabel
                      );
                    }}
                  >
                    Run analysis
                  </Button>
                ) : null}
              </div>
            ) : null,
        },
      ],
    });
  }

  return groups;
}

export { IndirectTaxFilingsStatusBreakdownTable };
